import { useState } from 'react';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { Ritual } from '../data/ingredients';
import {
  composeRitual,
  copyToClipboard,
  downloadFile,
  ritualToMarkdown,
  ritualToText,
  suggestedTitle,
} from '../lib/compose';
import styles from './RitualOutput.module.css';

interface RitualOutputProps {
  ritual: Ritual;
  saved: boolean;
  onSave: () => void;
  onEdit: () => void;
  onAnother: () => void;
}

export function RitualOutput({ ritual, saved, onSave, onEdit, onAnother }: RitualOutputProps) {
  const composed = composeRitual(ritual);
  const title = ritual.title?.trim() || suggestedTitle(ritual);
  const [feedback, setFeedback] = useState<string | null>(null);

  function ping(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 1800);
  }

  async function handleCopy() {
    const ok = await copyToClipboard(ritualToText(ritual, composed));
    ping(ok ? 'copied to clipboard' : 'copy failed');
  }

  function handleMarkdown() {
    const safe = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadFile(`ritual-${safe || 'composed'}.md`, ritualToMarkdown(ritual, composed), 'text/markdown');
    ping('downloaded markdown');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <>
      <article className={styles.page}>
        <header className={styles.header}>
          <span className={styles.ornament} aria-hidden="true">
            ❦
          </span>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.intent}>for {ritual.intent}</p>
        </header>

        <p className={styles.body}>{composed}</p>

        <footer className={styles.footer}>
          <span className={styles.divider} aria-hidden="true">
            ✦
          </span>
          <p className={styles.composedOn}>
            composed on {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </footer>
      </article>

      <div className={styles.actions}>
        {!saved && (
          <button type="button" className={styles.primary} onClick={onSave}>
            save this ritual
          </button>
        )}
        {saved && <span className={styles.savedBadge}>✓ saved</span>}
        <button type="button" className={styles.action} onClick={handlePrint}>
          print
        </button>
        <button type="button" className={styles.action} onClick={handleCopy}>
          copy as text
        </button>
        <button type="button" className={styles.action} onClick={handleMarkdown}>
          download .md
        </button>
        <button type="button" className={styles.action} onClick={onEdit}>
          edit
        </button>
        <button type="button" className={styles.action} onClick={onAnother}>
          compose another
        </button>
      </div>

      {feedback && (
        <div className={styles.feedback} role="status" aria-live="polite">
          {feedback}
        </div>
      )}

      <RelatedApps apps={RELATED_BY_APP['ritual-forge']} surface="cream" />
    </>
  );
}
